import { Search } from "lucide-react";
import type { CSSProperties, JSX, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { cx } from "../cx";

export interface CommandPaletteItem {
  id: string;
  /** Command text — also the filter target (case-insensitive substring). */
  label: string;
  /** Caps group heading; consecutive items sharing a group render together. */
  group?: string | undefined;
  /** Keyboard shortcut, rendered as a mono key cap ("G S"). */
  shortcut?: string | undefined;
  /** Lucide icon at 16px, aria-hidden. */
  icon?: ReactNode | undefined;
  /** Disabled commands stay visible but cannot be selected. */
  disabled?: boolean | undefined;
  /** Muted explanation rendered on disabled commands. */
  disabledReason?: string | undefined;
}

export interface CommandPaletteProps {
  open: boolean;
  /** Called on Escape and scrim click. */
  onClose?: (() => void) | undefined;
  /** Called with the command id on Enter or option click. */
  onSelect?: ((id: string) => void) | undefined;
  items?: ReadonlyArray<CommandPaletteItem> | undefined;
  placeholder?: string | undefined;
  /** Accessible name for the palette dialog. */
  label?: string | undefined;
  /** Copy for the no-results state. */
  emptyText?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Window-level Meta+K / Ctrl+K listener for opening the command palette.
 * Prevents the browser default and cleans up on unmount.
 */
export function useCommandK(handler: (event: KeyboardEvent) => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        handler(event);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handler]);
}

function filterItems(
  items: ReadonlyArray<CommandPaletteItem>,
  query: string,
): CommandPaletteItem[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return [...items];
  }
  return items.filter((item) => item.label.toLowerCase().includes(needle));
}

function firstEnabledIndex(items: ReadonlyArray<CommandPaletteItem>): number {
  const index = items.findIndex((item) => item.disabled !== true);
  return index >= 0 ? index : 0;
}

interface CommandSection {
  group: string | undefined;
  entries: Array<{ item: CommandPaletteItem; index: number }>;
}

function sectionize(items: ReadonlyArray<CommandPaletteItem>): CommandSection[] {
  const sections: CommandSection[] = [];
  for (const [index, item] of items.entries()) {
    const last = sections[sections.length - 1];
    if (last !== undefined && last.group === item.group) {
      last.entries.push({ item, index });
    } else {
      sections.push({ group: item.group, entries: [{ item, index }] });
    }
  }
  return sections;
}

/**
 * Command palette (Meta+K): scrim plus a centered solid panel — with Dialog,
 * one of the two legitimately floating studio layers. Typing filters by
 * label; ArrowUp/ArrowDown move the active option (wrapping), Enter selects,
 * Escape closes. Disabled commands render their reason, are skipped by
 * Enter, and stay visible. The active option carries the sanctioned 2px
 * selection inset. Ported from
 * design/tarski-kit/components/studio/CommandPalette.jsx.
 */
export function CommandPalette(props: CommandPaletteProps): JSX.Element | null {
  if (!props.open) {
    return null;
  }
  return <CommandPalettePanel {...props} />;
}

function CommandPalettePanel({
  onClose,
  onSelect,
  items = [],
  placeholder = "Type a command or filter…",
  label = "Command palette",
  emptyText = "No matching commands.",
  className,
  style,
}: CommandPaletteProps): JSX.Element {
  const baseId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(() => firstEnabledIndex(items));

  const filtered = filterItems(items, query);
  const listId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;
  const activeItem = filtered[activeIndex];
  const activeOptionId = activeItem === undefined ? undefined : optionId(activeIndex);

  // The palette mounts only while open, so focus once on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clicking the scrim itself (not the panel) closes.
  useEffect(() => {
    const scrim = scrimRef.current;
    if (scrim === null) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      if (event.target === scrim) {
        onClose?.();
      }
    };
    scrim.addEventListener("click", handleClick);
    return () => scrim.removeEventListener("click", handleClick);
  }, [onClose]);

  // Keep the active option visible inside the scrolling list.
  useEffect(() => {
    if (activeOptionId === undefined) {
      return;
    }
    document.getElementById(activeOptionId)?.scrollIntoView({ block: "nearest" });
  }, [activeOptionId]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setActiveIndex(firstEnabledIndex(filterItems(items, value)));
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
      return;
    }
    if (filtered.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((activeIndex + 1) % filtered.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((activeIndex - 1 + filtered.length) % filtered.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (activeItem !== undefined && activeItem.disabled !== true) {
        onSelect?.(activeItem.id);
      }
    }
  };

  const renderOption = (item: CommandPaletteItem, index: number) => (
    <button
      key={item.id}
      type="button"
      id={optionId(index)}
      role="option"
      aria-selected={index === activeIndex}
      aria-disabled={item.disabled === true ? true : undefined}
      tabIndex={-1}
      className="tk-command-palette__option"
      onClick={() => {
        if (item.disabled !== true) {
          onSelect?.(item.id);
        }
      }}
    >
      {item.icon === undefined ? null : (
        <span className="tk-command-palette__option-icon">{item.icon}</span>
      )}
      <span className="tk-command-palette__option-label">{item.label}</span>
      {item.disabled === true && item.disabledReason !== undefined ? (
        <>
          {/* Explicit spaces keep the accessible name readable; the flex
              layout ignores the whitespace boxes. */}{" "}
          <span className="tk-command-palette__reason">{item.disabledReason}</span>
        </>
      ) : null}
      <span className="tk-command-palette__spacer" />
      {item.shortcut === undefined ? null : (
        <>
          {" "}
          <kbd className="tk-command-palette__kbd">{item.shortcut}</kbd>
        </>
      )}
    </button>
  );

  return (
    <div ref={scrimRef} className="tk-command-palette-scrim" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cx("tk-command-palette", className)}
        style={style}
      >
        <div className="tk-command-palette__search">
          <Search size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={true}
            aria-controls={listId}
            aria-activedescendant={activeOptionId}
            aria-autocomplete="list"
            aria-label="Search commands"
            className="tk-command-palette__input"
            placeholder={placeholder}
            value={query}
            onChange={(event) => handleQueryChange(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="tk-command-palette__kbd">esc</kbd>
        </div>
        <div id={listId} role="listbox" aria-label="Commands" className="tk-command-palette__list">
          {sectionize(filtered).map((section) => {
            const sectionKey = `section-${section.entries[0]?.index ?? "none"}`;
            return (
              <Fragment key={sectionKey}>
                {section.group === undefined ? null : (
                  <div role="presentation" className="tk-command-palette__group-label">
                    {section.group}
                  </div>
                )}
                {section.entries.map(({ item, index }) => renderOption(item, index))}
              </Fragment>
            );
          })}
        </div>
        {filtered.length === 0 ? (
          <div className="tk-command-palette__empty">{emptyText}</div>
        ) : null}
      </div>
    </div>
  );
}
